import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { buildErrorMessage } from 'utils'
import { ApiError } from 'utils/error'
import {
  BrandPlanSchedule,
  BrandPlanScheduleCrosspost,
  BrandPlanTactic,
  BrandPlanTacticContents,
  ContentStrategyType,
  DailyFrequency,
  DeepPartial,
  WeekDays,
} from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { CrosspostStrategyInput } from 'hooks/queries/useUpdateBrandPlan'

export interface Data {
  schedule: BrandPlanSchedule
}

interface Props {
  brandPlanId: number
}

export interface BrandPlanScheduleCrosspostInput extends Omit<BrandPlanScheduleCrosspost, 'strategy'> {
  strategy: CrosspostStrategyInput
}

export type BrandPlanScheduleInput<S> = DeepPartial<Omit<S, 'id'>>

export type VariablesOf<S> = Omit<
  BrandPlanScheduleInput<S>,
  | 'tactics'
  | 'week_days'
  | 'daily_frequency'
  | 'days_before_review'
  | 'start_date'
  | 'instagram_contents'
  | 'facebook_contents'
  | 'linkedin_contents'
  | 'twitter_contents'
  | 'youtube_contents'
  | 'tiktok_contents'
  | 'crosspost_contents'
  | 'video_crosspost_contents'
  | 'seo_contents'
  | 'email_contents'
  | 'media_files'
> & {
  start_date: string
  week_days: WeekDays
  daily_frequency: DailyFrequency
  days_before_review: number
  prepare?: boolean
  content_strategy_type?: ContentStrategyType
  tactics?: BrandPlanTactic[]
  instagram_contents?: BrandPlanTacticContents
  facebook_contents?: BrandPlanTacticContents
  linkedin_contents?: BrandPlanTacticContents
  twitter_contents?: BrandPlanTacticContents
  email_contents?: BrandPlanTacticContents
  seo_contents?: BrandPlanTacticContents
  tiktok_contents?: BrandPlanTacticContents
  youtube_contents?: BrandPlanTacticContents
  crosspost_contents?: BrandPlanTacticContents
  video_crosspost_contents?: BrandPlanTacticContents
  google_my_business_contents?: BrandPlanTacticContents
  media_files?: Array<{ id: number }>
}

export type Variables = VariablesOf<BrandPlanScheduleCrosspostInput>

export const createSchedule = async ({
  workspaceId,
  brandPlanId,
  ...variables
}: Variables & Props & { workspaceId: number }) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_plans/${brandPlanId}/schedules`, {
    method: 'POST',
    body: variables,
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    const plainStringErrors = Array.isArray(res.data?.errors) && typeof res.data.errors[0] === 'string'

    const message =
      (plainStringErrors ? res.data.errors.join('. ') : buildErrorMessage(res.data.errors)) ||
      res.data?.error ||
      'An error occurred! Our team has been notified and we are looking into it.'

    throw plainStringErrors && res.data?.error_code ? new ApiError(message, res.data.error_code) : new Error(message)
  }

  return res.data
}

export interface Options extends Omit<UseMutationOptions<Data, Error, Variables>, 'mutationFn'> {}

export const useCreateSchedule = ({ brandPlanId }: Props, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<Data, Error, Variables>(
    (variables) =>
      createSchedule({
        ...variables,
        brandPlanId,
        workspaceId,
      }),
    options,
  )
}
